import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { FormsModule } from '@angular/forms';
import { StorageService } from '../../../services/storage.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DropdownPlacement, measureDropdownPlacement, PageScrollLock } from '../../../common/utils/dropdown-viewport';

@Component({
  selector: 'app-leaderboard',
  imports: [CommonModule, RouterModule, NavBarComponent, FormsModule],
  templateUrl: './leaderboard.component.html',
  styleUrl: './leaderboard.component.css'
})
export class LeaderboardComponent implements OnInit, AfterViewInit, OnDestroy {
  tabs = ['Today', 'Weekly', 'Monthly', 'All'];
  profitTabs: { key: 'profit' | 'volume'; label: string }[] = [
    { key: 'profit', label: 'Profit/Loss' },
    { key: 'volume', label: 'Volume' }
  ];
  activeProfitTab: 'profit' | 'volume' = 'profit';
  activeTab = 'All';
  isOpen = false;
  isTimeOpen = false;
  isProfitOpen = false;
  categoryMenu: DropdownPlacement = { maxHeight: 320, above: false };
  timeMenu: DropdownPlacement = { maxHeight: 320, above: false };
  selectedCategory = 'All Categories';
  selectedCategoryId: string | null = null;
  categories: any[] = [];

  // Leaderboard logic
  leaderboardData: any[] = [];
  topWinsData: any[] = [];
  userStats: any = null;
  loading = false;
  topWinsLoading = false;
  isLoadingMore = false;
  searchQuery = '';
  page = 1;
  pageSize = 20;
  hasMoreData = true;
  private searchSubject = new Subject<string>();
  private scrollLock = new PageScrollLock();

  // "Biggest wins this month" slider ------------------------------------------
  /** Widest the panel gets before the rows go back to a vertical list */
  private static readonly WINS_SLIDER_MAX_WIDTH = 991;
  /** Below this the slide is the whole track: one card, nothing peeking */
  private static readonly WINS_SINGLE_CARD_MAX_WIDTH = 575;
  /** Share of the track left showing the next card, so it reads as swipeable */
  private static readonly WINS_PEEK_RATIO = 0.16;
  /** Matches the track's `gap-5` */
  private static readonly WINS_GAP = 20;

  /** How long each win is shown before the carousel moves on */
  private static readonly WINS_AUTOPLAY_MS = 3000;

  /** The wins the slider runs through. Rebuilt with the data, so adding or
   *  removing a win re-lays the track and the dots without any other change */
  winsSlides: any[] = [];
  /** Which win is showing, and which dot is lit */
  winSlide = 0;
  /** Whether the second run of cards is rendered - only a slider with more than
   *  one card has anything to loop through */
  winsLoops = false;
  private winsResizeObserver?: ResizeObserver;
  private winsAutoplayTimer?: ReturnType<typeof setInterval>;
  private winsSettleTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('winsTrack') winsTrack?: ElementRef<HTMLElement>;
  @ViewChild('categoryTrigger') categoryTrigger?: ElementRef<HTMLElement>;
  @ViewChild('timeTrigger') timeTrigger?: ElementRef<HTMLElement>;

  constructor(
    private elementRef: ElementRef,
    private api: ApiService,
    private auth: AuthService,
    private storage: StorageService
  ) {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      this.fetchLeaderboard();
    });
  }

  ngOnInit() {
    this.loadCategories();
    this.fetchLeaderboard();
    this.fetchTopWins();
  }

  loadCategories() {
    const data = this.storage.getCategoryData();
    if (data && data.length > 0) {
      this.categories = [{ id: null, name: 'All Categories' }, ...data];
    } else {
      // Fallback if data is not yet loaded in storage
      this.categories = [{ id: null, name: 'All Categories' }];
      this.storage.categoryUpdate$.subscribe(updated => {
        if (updated) {
          this.categories = [{ id: null, name: 'All Categories' }, ...this.storage.getCategoryData()];
        }
      });
    }
  }

  fetchLeaderboard(isLoadMore: boolean = false) {
    // Reset pagination on fresh load
    if (!isLoadMore) {
      this.page = 1;
      this.leaderboardData = [];
      this.hasMoreData = true;
      this.loading = true;
      this.isLoadingMore = false;
    } else {
      if (this.isLoadingMore || !this.hasMoreData) {
        return;
      }
      this.isLoadingMore = true;
    }

    const params: any = {
      page: this.page,
      page_size: this.pageSize,
      time_filter: this.activeTab.toUpperCase(), // TODAY, WEEKLY, MONTHLY, ALL
      sort_by: this.activeProfitTab === 'profit' ? 'PNL' : 'VOLUME', // PNL, VOLUME
      sort_direction: 'DESC'
    };

    if (this.searchQuery) {
      params.search = this.searchQuery;
    }

    if (this.selectedCategoryId) {
      params.category_id = this.selectedCategoryId;
    }

    const userData = this.auth.getUserData();
    if (userData && (userData.id || userData.user_id)) {
      params.user_id = userData.id || userData.user_id;
    }

    this.api.getLeaderboard(params).subscribe({
      next: (res) => {
        const newData = (res.data.results || []).map((item: any) => ({
          ...item,
          user_id: item.user_id,
          profile_picture: item.profile_picture,
          created_at: item.created_at
        }));
        // console.log('fetchLeaderboard results mapped sample:', newData[0]);

        if (isLoadMore) {
          this.leaderboardData = [...this.leaderboardData, ...newData];
        } else {
          this.leaderboardData = newData;
        }

        this.userStats = res.data.user_stats ? {
          ...res.data.user_stats,
          user_id: res.data.user_stats.user_id || res.data.user_stats.id || res.data.user_stats.user_uuid,
          profile_picture: res.data.user_stats.profile_picture || res.data.user_stats.profile_pic
        } : null;

        // Check if there are more records
        this.hasMoreData = newData.length === this.pageSize;
        this.page++;

        this.loading = false;
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Error fetching leaderboard:', err);
        this.loading = false;
        this.isLoadingMore = false;

        if (!isLoadMore) {
          this.leaderboardData = []; // Fallback empty
        }
      }
    });
  }

  fetchTopWins() {
    this.topWinsLoading = true;

    this.api.getLeaderboardTopWins().subscribe({
      next: (res) => {
        this.topWinsData = (res?.data || []).map((item: any, index: number) => ({
          rank: index + 1,
          user_id: item.user_id,
          username: item.username,
          profile_picture: item.profile_pic,
          market_name: item.market_name,
          invested_amount: this.parseAmount(item.invested_amnt),
          winning_amount: this.parseAmount(item.winning_amount),
          profit: this.parseAmount(item.profit)
        }));
        this.setWinsSlides(this.topWinsData.slice(0, 5));
        this.topWinsLoading = false;
      },
      error: (err) => {
        console.error('Error fetching top wins:', err);
        this.topWinsData = [];
        this.setWinsSlides([]);
        this.topWinsLoading = false;
      }
    });
  }

  parseAmount(amount: any): number {
    if (!amount) return 0;
    if (typeof amount === 'number') return amount;
    return parseFloat(amount.toString().replace(/[^0-9.-]+/g, '')) || 0;
  }

  formatCurrency(value: any, formatOrShowPlus?: string | boolean): string {
    if (value === null || value === undefined) return '$0.00';
    const numValue = typeof value === 'number' ? value : parseFloat(value.toString().replace(/[^0-9.-]+/g, ''));
    if (isNaN(numValue)) return '$0.00';

    const showPlus = typeof formatOrShowPlus === 'boolean' ? formatOrShowPlus : false;
    const isNoDecimal = typeof formatOrShowPlus === 'string' && formatOrShowPlus.endsWith('-0');

    const formatted = Math.abs(numValue).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: isNoDecimal ? 0 : 2,
      maximumFractionDigits: isNoDecimal ? 0 : 2
    });

    if (numValue < 0) return '-' + formatted;
    return (showPlus ? '+' : '') + formatted;
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  get activeProfitLabel(): string {
    return this.profitTabs.find(tab => tab.key === this.activeProfitTab)?.label || 'Profit/Loss';
  }

  setProfitTab(tab: 'profit' | 'volume') {
    this.activeProfitTab = tab;
    this.fetchLeaderboard();
  }

  selectProfitTab(tab: 'profit' | 'volume') {
    this.closeDropdowns();
    this.setProfitTab(tab);
  }

  toggleProfitDropdown() {
    const open = !this.isProfitOpen;
    this.closeDropdowns();
    this.isProfitOpen = open;
    this.scrollLock.set(open);
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.fetchLeaderboard();
  }

  selectTimeTab(tab: string) {
    this.closeDropdowns();
    this.setActiveTab(tab);
  }

  selectCategory(category: any) {
    this.selectedCategory = category.name;
    this.selectedCategoryId = category.id;
    this.closeDropdowns();
    this.fetchLeaderboard();
  }

  toggleDropdown() {
    const open = !this.isOpen;
    this.closeDropdowns();
    this.isOpen = open;

    if (open) {
      this.categoryMenu = this.placeMenu(this.categoryTrigger, this.categoryMenu);
      this.scrollLock.set(true);
    }
  }

  toggleTimeDropdown() {
    const open = !this.isTimeOpen;
    this.closeDropdowns();
    this.isTimeOpen = open;

    if (open) {
      this.timeMenu = this.placeMenu(this.timeTrigger, this.timeMenu);
      this.scrollLock.set(true);
    }
  }

  /** Shuts every filter dropdown and gives the page its scrolling back */
  private closeDropdowns() {
    this.isOpen = false;
    this.isTimeOpen = false;
    this.isProfitOpen = false;
    this.scrollLock.set(false);
  }

  /**
   * @param trigger - The button the menu hangs from
   * @param fallback - Placement to keep if the button is not on screen yet
   * @returns Where and how tall the menu should open
   */
  private placeMenu(trigger: ElementRef<HTMLElement> | undefined, fallback: DropdownPlacement): DropdownPlacement {
    return trigger ? measureDropdownPlacement(trigger.nativeElement) : fallback;
  }

  /**
   * Rotating the phone or resizing the window changes the room around the
   * button, so an open menu is re-measured against the new viewport
   */
  /**
   * Takes a new set of wins, and re-lays the track around it.
   * @param {any[]} slides - The wins to run through
   * @returns {void}
   */
  private setWinsSlides(slides: any[]): void {
    this.winsSlides = slides;
    this.winSlide = 0;
    this.refreshWinsCarousel();
    // The copies are rendered by this pass, so the measuring waits for them
    setTimeout(() => {
      this.sizeWinsCards();
      this.winsTrack?.nativeElement.scrollTo({ left: 0, behavior: 'auto' });
    });
  }

  /** Whether the wins are laid out as a slider at this width */
  private isWinsSliderView(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${LeaderboardComponent.WINS_SLIDER_MAX_WIDTH}px)`).matches;
  }

  /**
   * Turns looping and autoplay on where they apply, and off where they do not:
   * a single card has nowhere to go, and the desktop list is not a slider.
   * @returns {void}
   */
  private refreshWinsCarousel(): void {
    const loops = this.isWinsSliderView() && this.winsSlides.length > 1;
    this.winsLoops = loops;

    if (loops) {
      this.startWinsAutoplay();
    } else {
      this.stopWinsAutoplay();
      this.winSlide = 0;
    }
  }

  /** Restarts the countdown, so a card picked by hand gets a full turn */
  private startWinsAutoplay(): void {
    this.stopWinsAutoplay();
    this.winsAutoplayTimer = setInterval(
      () => this.goToWinSlide(this.winSlide + 1),
      LeaderboardComponent.WINS_AUTOPLAY_MS
    );
  }

  private stopWinsAutoplay(): void {
    if (this.winsAutoplayTimer) {
      clearInterval(this.winsAutoplayTimer);
      this.winsAutoplayTimer = undefined;
    }
  }

  ngAfterViewInit(): void {
    const track = this.winsTrack?.nativeElement;
    if (!track) {
      return;
    }
    this.sizeWinsCards();
    this.refreshWinsCarousel();
    // The panel changes width with the page, not just with the window: the
    // observer catches both without polling
    this.winsResizeObserver = new ResizeObserver(() => this.sizeWinsCards());
    this.winsResizeObserver.observe(track);
  }

  /**
   * Sets how wide one card is, measured from the track rather than assumed, so
   * the sliver of the next card scales with the screen instead of being a fixed
   * size. At the narrowest widths the card takes the whole track.
   * @returns {void}
   */
  private sizeWinsCards(): void {
    const track = this.winsTrack?.nativeElement;
    if (!track) {
      return;
    }

    const trackWidth = track.clientWidth;
    if (trackWidth === 0) {
      return;
    }

    const showsPeek = window.innerWidth > LeaderboardComponent.WINS_SINGLE_CARD_MAX_WIDTH;
    const cardWidth = showsPeek
      ? Math.max(0, Math.round(trackWidth * (1 - LeaderboardComponent.WINS_PEEK_RATIO) - LeaderboardComponent.WINS_GAP))
      : trackWidth;

    track.style.setProperty('--wins-card-width', `${cardWidth}px`);
    // A resize moves every card, so the dot has to follow where the track ended up
    this.onWinsScroll();
  }

  /** Distance from one card's left edge to the next one's */
  private get winsSlideStride(): number {
    const track = this.winsTrack?.nativeElement;
    if (!track) {
      return 0;
    }
    const cardWidth = parseFloat(getComputedStyle(track).getPropertyValue('--wins-card-width')) || track.clientWidth;
    return cardWidth + LeaderboardComponent.WINS_GAP;
  }

  /**
   * Lights the dot for whichever card the swipe came to rest on.
   * @returns {void}
   */
  onWinsScroll(): void {
    const track = this.winsTrack?.nativeElement;
    const stride = this.winsSlideStride;
    if (!track || stride <= 0) {
      return;
    }
    this.winSlide = Math.max(0, Math.round(track.scrollLeft / stride));

    // Wait for the slide to come to rest before deciding whether the track is
    // sitting on a copy: doing it mid-scroll would yank the card being animated
    clearTimeout(this.winsSettleTimer);
    this.winsSettleTimer = setTimeout(() => this.rewindWinsLoop(), 140);
  }

  /**
   * Once a slide settles on the second run of cards, the track is moved back by
   * one full run. The card under it is the same one, so nothing moves on screen
   * - it just puts the whole run ahead of the slider again.
   * @returns {void}
   */
  private rewindWinsLoop(): void {
    const track = this.winsTrack?.nativeElement;
    const count = this.winsSlides.length;
    if (!track || !this.winsLoops || count === 0 || this.winSlide < count) {
      return;
    }

    const stride = this.winsSlideStride;
    this.winSlide -= count;
    // Instant, and with snapping out of the way: this is a reposition onto an
    // identical frame, not a slide the user should see
    const behavior = track.style.scrollBehavior;
    const snap = track.style.scrollSnapType;
    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';
    track.scrollLeft = this.winSlide * stride;
    // Read back before restoring, so the browser applies the new position
    // against the un-snapped track rather than animating to it
    void track.offsetWidth;
    track.style.scrollBehavior = behavior;
    track.style.scrollSnapType = snap;
  }

  /**
   * Slides to a card when its dot is tapped.
   * @param {number} index - Position of the card in the list
   * @returns {void}
   */
  goToWinSlide(index: number): void {
    const track = this.winsTrack?.nativeElement;
    if (!track || this.winsSlides.length === 0) {
      return;
    }

    // Past the last card the slide carries on into the copies rather than
    // rewinding, which is what keeps 4 -> 1 going the same way as 1 -> 2
    const target = this.winsLoops
      ? index % (this.winsSlides.length * 2)
      : Math.min(Math.max(index, 0), this.winsSlides.length - 1);

    this.winSlide = target;
    track.scrollTo({ left: target * this.winsSlideStride, behavior: 'smooth' });
  }

  /** Which dot is lit: positions in the copied run map back onto the real cards */
  get activeWinDot(): number {
    const count = this.winsSlides.length;
    return count > 0 ? this.winSlide % count : 0;
  }

  /**
   * Lets a card the user picked stay put for a full turn instead of being
   * carried off by a countdown that was already half spent.
   * @param {number} index - The card to slide to
   * @returns {void}
   */
  selectWinSlide(index: number): void {
    this.goToWinSlide(index);
    if (this.winsLoops) {
      this.startWinsAutoplay();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.sizeWinsCards();
    this.refreshWinsCarousel();
    if (this.isOpen) {
      this.categoryMenu = this.placeMenu(this.categoryTrigger, this.categoryMenu);
    }
    if (this.isTimeOpen) {
      this.timeMenu = this.placeMenu(this.timeTrigger, this.timeMenu);
    }
  }

  ngOnDestroy() {
    // Leaving the page with a dropdown open must not strand the lock on <body>
    this.closeDropdowns();
    this.winsResizeObserver?.disconnect();
    this.stopWinsAutoplay();
    clearTimeout(this.winsSettleTimer);
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdowns();
    }
  }

  shortenUsername(username: string): string {
    if (!username) return 'Anonymous';
    return username.length > 15 ? username.slice(0, 15) + '...' : username;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const pos = (document.documentElement.scrollTop || document.body.scrollTop) + document.documentElement.offsetHeight;
    const max = document.documentElement.scrollHeight;
    if (pos >= max - 200) {
      if (this.hasMoreData && !this.isLoadingMore && !this.loading) {
        this.fetchLeaderboard(true);
      }
    }
  }
}
